/**
 * Self-serve email preferences. Same toggles as the claimant SWA's /preferences page,
 * plus admin/payroll-only ones gated to those roles.
 */
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";

export default function EmailPrefsAdmin() {
  const qc = useQueryClient();
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: api.me });
  const { data: prefs, isLoading, error } = useQuery({
    queryKey: ["me-email-prefs"],
    queryFn: () => api.admin.getEmailPrefs(),
  });
  const save = useMutation({
    mutationFn: (next: Record<string, boolean>) => api.admin.setEmailPrefs(next),
    onSuccess: (next) => qc.setQueryData(["me-email-prefs"], next),
  });

  const roles = me?.roles ?? [];
  const isApprover = roles.includes("Approver") || roles.includes("Admin");
  const isPayroll = roles.includes("Payroll") || roles.includes("Admin");

  return (
    <div className="space-y-6 max-w-2xl">
      <Link to="/admin" className="text-sm text-slate-500 hover:text-slate-700">← Back to admin</Link>

      <div>
        <h1 className="text-3xl font-semibold text-liaise-navy">Email preferences</h1>
        <p className="text-slate-600 mt-1">Choose which expense notifications you want to receive.</p>
      </div>

      {isLoading && <div className="text-slate-500">Loading…</div>}
      {error && <div className="text-red-600 text-sm">{(error as Error).message}</div>}

      {prefs && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-5">
          <Section title="My claims">
            <Toggle label="When my claim is approved" desc="Email after manager approval and after payroll approval." checked={prefs.notify_my_approved} onChange={(v) => save.mutate({ notify_my_approved: v })} />
            <Toggle label="When my claim is rejected" desc="So you know to review the rejection reason and resubmit." checked={prefs.notify_my_rejected} onChange={(v) => save.mutate({ notify_my_rejected: v })} />
            <Toggle label="When my claim is paid" desc="Confirmation that payment has been processed." checked={prefs.notify_my_paid} onChange={(v) => save.mutate({ notify_my_paid: v })} />
            <Toggle label="When my claim is rolled back" desc="If a paid or approved claim is reverted to fix something (eg bank details)." checked={prefs.notify_my_reverted} onChange={(v) => save.mutate({ notify_my_reverted: v })} />
            <Toggle label="Reminders for old drafts" desc="Daily nudge if you have unsubmitted drafts older than 14 days." checked={prefs.notify_my_reminders} onChange={(v) => save.mutate({ notify_my_reminders: v })} />
          </Section>

          {isApprover && (
            <Section title="As a manager">
              <Toggle label="When a claim needs my approval" desc="Email when one of your team submits a claim, plus reminders if you haven't actioned it within 3 days." checked={prefs.notify_when_to_review} onChange={(v) => save.mutate({ notify_when_to_review: v })} />
            </Section>
          )}

          {isPayroll && (
            <Section title="As payroll">
              <Toggle label="When a claim is ready for payroll" desc="Email when a manager approves a claim and it lands in the payroll queue." checked={prefs.notify_payroll_queue} onChange={(v) => save.mutate({ notify_payroll_queue: v })} />
            </Section>
          )}

          {save.isPending && <div className="text-xs text-slate-500">Saving…</div>}
          {save.error && <div className="text-xs text-red-600">{(save.error as Error).message}</div>}
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="font-medium text-liaise-navy mb-3 text-sm uppercase tracking-wide">{title}</h2>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Toggle({ label, desc, checked, onChange }: { label: string; desc: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-4 w-4" />
      <div>
        <div className="font-medium text-slate-800">{label}</div>
        <div className="text-sm text-slate-500 mt-0.5">{desc}</div>
      </div>
    </label>
  );
}
